"use client";

import * as React from "react";
import Script from "next/script";
import { useLocale } from "next-intl";
import { Overline } from "@repo/ui/overline";
import { PagebuilderType } from "@/lib/types";
import { getI18nString, getI18nValue } from "@/lib/utils";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";

// nter.io ships a <nter-app-form> custom element bootstrapped by this script
// (typed in src/types/jsx.d.ts). Rendering it inline — the way the current site
// does — lets the form run in the page's own context, so it works fully and
// grows with its content. The script URL is fixed here, never editor input, so
// there is no script-injection surface: editors only supply the identifiers.
const NTER_SCRIPT_SRC = "https://apply.nter.io/nter-app-form.js";

export type NterFormProps = PagebuilderType<"nterForm">;

/**
 * nter.io Enquiry Form Block
 * Renders an nter.io application/enquiry form inline from its identifiers.
 */
export function NterForm({
  overline,
  headline,
  bodyCopy,
  locationCode,
  offeringCode,
  embedKey,
  title,
}: NterFormProps): JSX.Element | null {
  const locale = useLocale();
  const headlineText = getI18nString(headline, "", locale);
  const overlineText = getI18nString(overline, "", locale);
  const body = getI18nValue(bodyCopy);

  const hasIntro = Boolean(overlineText || headlineText || body);

  // Without the identifiers there is nothing to render.
  if (!locationCode || !offeringCode || !embedKey) {
    return null;
  }

  return (
    <section className="py-16 tablet:py-28" data-testid="block-nter-form">
      <div className="container-grid">
        <div className="col-span-full grid grid-cols-subgrid gap-y-6 tablet:gap-y-12">
          {hasIntro && (
            <div className="col-span-full tablet:col-span-6 desktop:col-span-8">
              {overlineText && (
                <Overline as="p" className="mb-4 tablet:mb-6">
                  {overlineText}
                </Overline>
              )}
              {headlineText && (
                <h2 className="typography-mobile-heading-h5 tablet:typography-desktop-heading-h5 text-text-primary mb-4 tablet:mb-6">
                  {headlineText}
                </h2>
              )}
              {body && (
                <RichTextCondensed
                  richText={body}
                  className="text-text-secondary"
                />
              )}
            </div>
          )}
          <div
            className="col-span-full"
            role="group"
            aria-label={title ?? "Enquiry form"}
          >
            <Script src={NTER_SCRIPT_SRC} strategy="afterInteractive" />
            <nter-app-form
              location-code={locationCode}
              offering-code={offeringCode}
              embed-key={embedKey}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
